import { Button } from "@timeghost/ui/shadcn/button";
import { Skeleton } from "@timeghost/ui/shadcn/skeleton";
import { Pencil, Trash2 } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { BillableTableCell, BillableTableColumnHeader } from "@/components/ui/billable-toggle";
import TableScrollX from "@/components/ui/table-scroll-x";
import VirtualScrollList from "@/components/ui/virtual-scroll-list";
import { useProfileFormat } from "@/lib/account/use-profile-format";
import { formatDuration } from "@/lib/timer/parse-ms";
import { formatProjectHourClock } from "@/lib/timer/utils";
import { cn } from "@/lib/utils";
import type { ProjectDetailTimeRow } from "@/queries/projects/useProjectDetailTimes.query";

export const HOURS_ROW_ESTIMATE = 56;
export const HOURS_GRID_CLASS =
	"grid min-w-[56rem] grid-cols-[minmax(12rem,1.4fr)_minmax(7rem,0.8fr)_minmax(9rem,0.9fr)_minmax(7rem,0.9fr)_5.5rem_4.5rem_2.5rem] items-stretch gap-2 px-3";

/**
 * Report user history: fixed-width columns (roughly content-sized) spread with `justify-between`, so the
 * first column sits at the left edge, the last at the right edge, and the free space is split equally
 * between all columns. `gap-x-4` is the minimum gap when the panel is narrow.
 */
export const PROJECT_REPORT_TIME_GRID_CLASS =
	"grid w-full min-w-[46rem] grid-cols-[12rem_7rem_7rem_7.5rem_4rem_2.5rem] items-center justify-between gap-x-4 gap-y-1 text-left [&>*]:min-w-0";
/** Last column: right-aligned so its right edge meets the row end. */
export const PROJECT_REPORT_LAST_CELL = "justify-self-end text-right";

export interface HoursTaskGroup {
	readonly durationSeconds: number;
	readonly items: readonly ProjectDetailTimeRow[];
	readonly key: string;
	readonly title: string;
}

type HoursFlatRow =
	| { readonly kind: "group"; readonly group: HoursTaskGroup }
	| { readonly kind: "entry"; readonly item: ProjectDetailTimeRow };

const SKELETON_ROWS = 8;

const groupDurations = (
	items: readonly ProjectDetailTimeRow[]
): { readonly billable: number; readonly notBillable: number; readonly total: number } => {
	let billable = 0;
	let notBillable = 0;
	for (const item of items) {
		if (item.billable) {
			billable += item.durationSeconds;
		} else {
			notBillable += item.durationSeconds;
		}
	}
	return { billable, notBillable, total: billable + notBillable };
};

const flattenGroups = (groups: readonly HoursTaskGroup[]): HoursFlatRow[] => {
	const rows: HoursFlatRow[] = [];
	for (const group of groups) {
		rows.push({ kind: "group", group });
		for (const item of group.items) {
			rows.push({ kind: "entry", item });
		}
	}
	return rows;
};

interface ProjectHoursTableProps {
	readonly groups: readonly HoursTaskGroup[];
	readonly isLoading: boolean;
	readonly onDelete: (row: ProjectDetailTimeRow) => void;
	readonly onEdit: (row: ProjectDetailTimeRow) => void;
}

const HoursTableHeader = (): JSX.Element => {
	const { t } = useTranslation();
	return (
		<div
			className={cn(
				HOURS_GRID_CLASS,
				"shrink-0 border-border border-b bg-well py-2 font-semibold text-foreground text-xs"
			)}>
			<div>{t("timer.description")}</div>
			<div>{t("settings.projects.memberUser")}</div>
			<div>{t("reports.exportColumn.date")}</div>
			<div>{t("timer.table.time")}</div>
			<div className="text-center">{t("timer.duration")}</div>
			<div className="text-center">
				<BillableTableColumnHeader />
			</div>
			<div />
		</div>
	);
};

export default function ProjectHoursTable({
	groups,
	isLoading,
	onEdit,
	onDelete,
}: ProjectHoursTableProps): JSX.Element {
	const { t, i18n } = useTranslation();
	const profileFormat = useProfileFormat();
	const rows = flattenGroups(groups);
	const empty = !isLoading && rows.length === 0;
	const listCount = isLoading ? SKELETON_ROWS : empty ? 1 : rows.length;

	return (
		<div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-border">
			<TableScrollX>
				<div className="flex h-full min-h-0 min-w-max flex-col">
					<HoursTableHeader />
					<VirtualScrollList className="h-0 flex-1" count={listCount} estimateSize={HOURS_ROW_ESTIMATE}>
						{(index) => {
							if (isLoading) {
								return (
									<div className={cn(HOURS_GRID_CLASS, "border-border border-b")} key={`hours-skel-${index}`}>
										<div className="flex min-h-12 items-center border-border border-l pl-4">
											<Skeleton className="h-4 w-40" />
										</div>
										<div className="flex min-h-12 items-center">
											<Skeleton className="h-4 w-24" />
										</div>
										<div className="flex min-h-12 items-center">
											<Skeleton className="h-4 w-16" />
										</div>
										<div className="flex min-h-12 items-center">
											<Skeleton className="h-4 w-28" />
										</div>
										<div className="flex min-h-12 items-center justify-center">
											<Skeleton className="h-4 w-12" />
										</div>
										<div className="flex min-h-12 items-center justify-center">
											<Skeleton className="size-3.5 rounded-sm" />
										</div>
										<div className="flex min-h-12 items-center">
											<Skeleton className="size-8 rounded-md" />
										</div>
									</div>
								);
							}
							if (empty) {
								return (
									<div
										className="flex min-h-24 items-center justify-center px-3 py-8 text-center text-muted-foreground text-sm"
										key="hours-empty">
										{t("settings.projects.hours.empty")}
									</div>
								);
							}
							const row = rows[index];
							if (!row) {
								return null;
							}
							if (row.kind === "group") {
								const durations = groupDurations(row.group.items);
								return (
									<div
										className="flex min-h-10 items-center justify-between gap-2 border-border border-b bg-well px-3 py-2"
										key={`group-${row.group.key}`}>
										<span className="min-w-0 truncate font-medium text-xs">{row.group.title}</span>
										<span className="flex shrink-0 items-center gap-1 text-muted-foreground text-xs">
											{durations.billable > 0 ? (
												<>
													<span className="text-foreground tabular-nums">$ {formatDuration(durations.billable)}</span>
													<span className="tabular-nums">/</span>
													<span className="text-foreground/70 tabular-nums">
														{formatDuration(durations.notBillable)}
													</span>
													<span className="tabular-nums">/</span>
												</>
											) : null}
											<span className="text-foreground tabular-nums">{formatDuration(durations.total)}</span>
										</span>
									</div>
								);
							}
							const item = row.item;
							const clock = formatProjectHourClock(item, i18n.language, profileFormat.settings.timeFormat24h);
							return (
								<div className={cn(HOURS_GRID_CLASS, "border-border border-b")} key={item.id}>
									<div className="flex min-h-12 min-w-0 items-center border-border border-l pl-4">
										<span className="truncate text-sm">{item.name || t("timer.noDescription")}</span>
									</div>
									<div className="flex min-h-12 min-w-0 items-center">
										<span className="truncate text-muted-foreground text-sm">{item.userName}</span>
									</div>
									<div className="flex min-h-12 min-w-0 items-center">
										<span className="truncate text-muted-foreground text-xs">
											{profileFormat.dateWithWeekday(item.start)}
										</span>
									</div>
									<div className="flex min-h-12 min-w-0 items-center">
										<span className="truncate text-muted-foreground text-xs tabular-nums">{clock}</span>
									</div>
									<div className="flex min-h-12 items-center justify-center">
										<span className="text-sm tabular-nums">{formatDuration(item.durationSeconds)}</span>
									</div>
									<div className="flex min-h-12 items-center justify-center">
										<BillableTableCell billable={item.billable} />
									</div>
									<div className="flex min-h-12 items-center justify-end gap-0.5">
										{item.currentUserCanEdit ? (
											<>
												<Button
													aria-label={t("settings.projects.edit")}
													onClick={() => onEdit(item)}
													size="icon-sm"
													type="button"
													variant="ghost">
													<Pencil className="size-3.5" />
												</Button>
												<Button
													aria-label={t("settings.projects.delete")}
													onClick={() => onDelete(item)}
													size="icon-sm"
													type="button"
													variant="ghost">
													<Trash2 className="size-3.5" />
												</Button>
											</>
										) : null}
									</div>
								</div>
							);
						}}
					</VirtualScrollList>
				</div>
			</TableScrollX>
		</div>
	);
}
